import { Paper } from "@mui/material";
import { PaperProps } from "@mui/material/Paper";
import { Theme } from '@mui/material/styles';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import { GlobalClassKeys } from "assets/jss/common-styles";
import classNames from "classnames";
import { MuiColors } from "components/MuiExtras/Typography/MuiColor";
import * as React from "react";


const style = (theme: Theme) => createStyles({
    gutters: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },

    normal: {
        padding: theme.spacing(2),
    },

    dense: {
        padding: theme.spacing(),
    },

    fullHeight: {
        height: "100%",
    },

    borderSquare: {
        borderRadius: "0",
    },

    borderRoundAccented: {
        borderRadius: "16px",
    },

    borderColor: {
        borderWidth: "1px",
        borderStyle: "solid",
    },

    border_muted: {
        borderColor: theme.genie.palette.muted.main
    },
    border_mutedLight: {
        borderColor: theme.genie.palette.muted.light
    },
    border_mutedDark: {
        borderColor: theme.genie.palette.muted.dark
    },
    border_primary: {
        borderColor: theme.palette.primary.main
    },
    border_secondary: {
        borderColor: theme.palette.secondary.main
    },
    border_error: {
        borderColor: theme.palette.error.main
    },
    border_info: {
        borderColor: theme.genie.palette.info.main
    },
    border_success: {
        borderColor: theme.genie.palette.success.main
    },
    border_warning: {
        borderColor: theme.genie.palette.warning.main
    },
    border_white: {
        borderColor: theme.palette.common.white
    },
    border_black: {
        borderColor: theme.palette.common.black
    },
    border_inherit: {
        borderColor: "inherit"
    },
    border_transparent: {
        borderColor: "transparent"
    },


    muted: {
        backgroundColor: theme.genie.palette.muted.main
    },
    mutedLight: {
        backgroundColor: theme.genie.palette.muted.light
    },
    mutedDark: {
        backgroundColor: theme.genie.palette.muted.dark
    },
    primary: {
        backgroundColor: theme.palette.primary.light
    },
    secondary: {
        backgroundColor: theme.palette.secondary.light
    },
    error: {
        backgroundColor: theme.palette.error.light
    },
    info: {
        backgroundColor: theme.genie.palette.info.light
    },
    success: {
        backgroundColor: theme.genie.palette.success.light
    },
    warning: {
        backgroundColor: theme.genie.palette.warning.light
    },
    white: {
        backgroundColor: theme.palette.common.white
    },
    black: {
        backgroundColor: theme.palette.common.black
    },
    inherit: {
    },
    transparent: {
    },

});

interface IProps {
    dense?: boolean;
    disableGutters?: boolean;
    disablePadding?: boolean;
    fullHeight?: boolean;
    borderVariant?: "square" | "round" | "accentedRound";
    borderColor?: MuiColors;
    paperColor?: MuiColors;
    muted?: boolean;
}

type Props = IProps & PaperProps & WithStyles<typeof style>;

const MuiPaper: React.FC<Props> = (props) => {

    const { classes, dense, disablePadding, disableGutters, elevation, fullHeight, muted, borderColor, borderVariant, paperColor, children, ...rest } = props;

    return (
        <Paper
            classes={
                {
                    root: classNames({
                        [GlobalClassKeys.mutedBackground]: muted,
                         [classes.gutters]: !disableGutters,
                        [classes.normal]: !dense && !disablePadding,
                        [classes.dense]: dense && !disablePadding,
                        [classes.fullHeight]: fullHeight,
                        [classes.borderRoundAccented]: borderVariant == "accentedRound",
                        [classes.borderSquare]: borderVariant == "square",
                        [classes.borderColor]: borderColor != null,
                        [classes[`border_${borderColor}`]]: borderColor != null,
                        [classes[paperColor]]: paperColor
                    })
                }
            }
            elevation={fullHeight ? 0 : elevation}
            {...rest}
        >
            {children}
        </Paper>
    );
};

MuiPaper.defaultProps = {
    dense: false,
    disableGutters: false,
    disablePadding: false,
    fullHeight: false,
    borderVariant: "round",
    borderColor: null,
    muted: false
};

export default withStyles(style)(MuiPaper);
